import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '@/shared/api/client';
import {
  operations,
  products,
  type OperationDetail,
  type OperationInput,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import { useAuth } from '@/shared/auth/useAuth';
import {
  formatCpfCnpj,
  formatDate,
  formatDateTime,
  formatMoney,
  formatRate,
  moneyInputError,
  moneyToInput,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
  toMoney,
} from '@/shared/lib/format';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/ui/Card';
import { CopyButton } from '@/shared/ui/CopyButton';
import { Field } from '@/shared/ui/Field';
import { MoneyField } from '@/shared/ui/MoneyField';
import { Select } from '@/shared/ui/Select';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { useToast } from '@/shared/ui/useToast';

import { fieldErrorOf } from '../shared/fieldError';
import { ContractualTermsEditor } from './ContractualTermsEditor';
import { CoveragesSection } from './CoveragesSection';
import {
  cotacaoMinutaAberta,
  cotacaoObjetoAberto,
  permissoesDaCotacao,
} from './cotacao';
import { DraftPrintDialog } from './DraftPrintDialog';
import { InstallmentsSection } from './InstallmentsSection';
import { ParticularClausesSection } from './ParticularClausesDialog';
import { QuoteConfigSection, type QuoteConfigValues } from './QuoteConfigSection';

interface Formulario {
  valor: string;
  inicio: string;
  prazo: string;
  fim: string;
  complementar: string;
  inicioComplementar: string;
  nomeFantasia: string;
  premioPrincipal: string;
  premioComplementar: string;
  premioMinimo: string;
  comissaoMaxima: string;
  premioZerado: boolean;
  premioManual: boolean;
  configuracao: QuoteConfigValues;
  /** Codigo do quadro de distribuicao (NCT-215). */
  distribuicao: string;
}

function formularioDe(o: OperationDetail): Formulario {
  return {
    valor: moneyToInput(o.insured_amount),
    inicio: o.coverage_start?.slice(0, 10) ?? '',
    prazo: o.term_days ? String(o.term_days) : '',
    fim: o.coverage_end?.slice(0, 10) ?? '',
    complementar: o.complementary_modality?.id ?? '',
    inicioComplementar: o.complementary_coverage_start ?? '',
    nomeFantasia: o.insured_trade_name?.id ?? '',
    premioPrincipal: dinheiroNoCampo(o.principal_premium),
    premioComplementar: dinheiroNoCampo(o.complementary_premium),
    premioMinimo: dinheiroNoCampo(o.min_premium),
    comissaoMaxima:
      o.max_commission_bps === null || o.max_commission_bps === undefined
        ? ''
        : rateToInput(o.max_commission_bps),
    premioZerado: o.premium_set_to_zero ?? false,
    premioManual: o.premium_set_manually ?? false,
    distribuicao: o.distribution_code ?? '',
    configuracao: {
      premium_locked: o.premium_locked ?? false,
      big_risk: o.big_risk ?? false,
      monitoring: o.monitoring ?? false,
      step_in: o.step_in ?? false,
    },
  };
}

/** Valor opcional no campo: o zero gravado aparece como zero, e nao como vazio. */
function dinheiroNoCampo(valor: Money | null | undefined): string {
  if (!valor) return '';
  return valor.amount === 0 ? '0' : moneyToInput(valor);
}

function dinheiroOuNulo(texto: string): Money | null {
  return texto.trim() === '' ? null : toMoney(texto);
}

function mesmoDinheiro(a: Money | null | undefined, b: Money | null): boolean {
  return (a?.amount ?? null) === (b?.amount ?? null);
}

/**
 * Aba "Resumo da cotacao" (COT-031 a COT-062, COT-078 a COT-082), na ordem do OnPoint:
 * cabecalho, Modalidade Principal, Coberturas Adicionais, Configuracao e os valores que
 * dependem de regra pendente, digitados por quem tem a alcada.
 *
 * Nenhum valor e calculado aqui nem no servidor: o premio da operacao continua o do
 * valor garantido pela taxa, e os premios da cotacao sao digitados. "Atualizar
 * cotacao" grava so o que mudou. Cada campo fica desabilitado quando a etapa ou a
 * permissao nao permitem, mas quem decide e o servidor.
 */
export function QuoteSummaryTab({ operacao }: { operacao: OperationDetail }) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Formulario>(() => formularioDe(operacao));
  const [base, setBase] = useState(operacao);
  const [baixandoMinuta, setBaixandoMinuta] = useState(false);
  // A resposta nova do servidor (depois de gravar ou de mover a etapa) recompoe o
  // formulario: o que a tela mostra e sempre o que esta gravado.
  if (base !== operacao) {
    setBase(operacao);
    setForm(formularioDe(operacao));
  }

  const edita = can('operacoes.operacao.update') && !operacao.is_terminal;
  const objeto = cotacaoObjetoAberto(operacao.stage);
  const minuta = cotacaoMinutaAberta(operacao.stage);
  const podeVigencia = edita && can(permissoesDaCotacao.vigencia);
  const podeValor = podeVigencia && objeto;
  const podeDatas = podeVigencia && minuta;
  const podeComplementar = edita && objeto;
  const podeNomeFantasia = edita && minuta && can(permissoesDaCotacao.segurado);
  const podeConfigurar = edita && minuta && can(permissoesDaCotacao.configuracao);
  const podePremio = edita && minuta && can(permissoesDaCotacao.premio);
  // "Download da minuta" (COT-083): baixa a vigente ou gera pelo modelo da empresa.
  const podeVerMinuta = can('minutas.minuta.view') || can('minutas.minuta.create');

  const listaProdutos = useQuery({
    queryKey: ['products'],
    queryFn: ({ signal }) => products.list(signal),
    enabled: podeComplementar,
  });
  const opcoesComplementar = useMemo(() => {
    const opcoes = (listaProdutos.data ?? [])
      .flatMap((produto) => produto.modalities)
      .filter(
        (m) =>
          m.id !== operacao.modality.id &&
          (m.status === 'ativo' || m.id === form.complementar),
      )
      .map((m) => ({ value: m.id, label: m.name }));
    const atual = operacao.complementary_modality;
    if (atual && !opcoes.some((o) => o.value === atual.id)) {
      opcoes.unshift({ value: atual.id, label: atual.label });
    }
    return opcoes;
  }, [listaProdutos.data, operacao, form.complementar]);

  const atualizar = useMutation({
    mutationFn: (input: OperationInput) => operations.update(operacao.id, input),
    onSuccess: () => {
      toast.success('Cotacao atualizada.');
      void queryClient.invalidateQueries({ queryKey: ['operations'] });
    },
  });

  const erros = {
    valor: moneyInputError(form.valor),
    premioPrincipal: moneyInputError(form.premioPrincipal),
    premioComplementar: moneyInputError(form.premioComplementar),
    premioMinimo: moneyInputError(form.premioMinimo),
    comissaoMaxima: rateInputError(form.comissaoMaxima),
  };
  const invalido = Object.values(erros).some((erro) => erro !== undefined);

  // So o que mudou vai no pedido: o servidor pede a permissao de cada grupo, e mandar
  // o que nao mudou pediria uma alcada que a pessoa talvez nao tenha.
  function pedido(): OperationInput {
    const original = formularioDe(operacao);
    const input: OperationInput = {};
    if (form.valor !== original.valor)
      input.insured_amount = toMoney(form.valor || '0');
    if (form.inicio !== original.inicio) input.coverage_start = form.inicio;
    if (form.fim !== original.fim) input.coverage_end = form.fim;
    if (form.prazo !== original.prazo) {
      input.term_days = form.prazo ? Number(form.prazo) : null;
    }
    if (form.complementar !== original.complementar) {
      input.complementary_modality_id = form.complementar;
    }
    if (form.inicioComplementar !== original.inicioComplementar) {
      input.complementary_coverage_start = form.inicioComplementar;
    }
    if (form.nomeFantasia !== original.nomeFantasia) {
      input.insured_trade_name_id = form.nomeFantasia;
    }
    for (const chave of [
      'premium_locked',
      'big_risk',
      'monitoring',
      'step_in',
    ] as const) {
      if (form.configuracao[chave] !== original.configuracao[chave]) {
        input[chave] = form.configuracao[chave];
      }
    }
    const principal = dinheiroOuNulo(form.premioPrincipal);
    if (!mesmoDinheiro(operacao.principal_premium, principal)) {
      input.principal_premium = principal;
    }
    const complementar = dinheiroOuNulo(form.premioComplementar);
    if (!mesmoDinheiro(operacao.complementary_premium, complementar)) {
      input.complementary_premium = complementar;
    }
    const minimo = dinheiroOuNulo(form.premioMinimo);
    if (!mesmoDinheiro(operacao.min_premium, minimo)) input.min_premium = minimo;
    if (form.comissaoMaxima !== original.comissaoMaxima) {
      input.max_commission_bps = form.comissaoMaxima
        ? rateToBasisPoints(form.comissaoMaxima)
        : null;
    }
    if (form.premioZerado !== original.premioZerado) {
      input.premium_set_to_zero = form.premioZerado;
    }
    if (form.premioManual !== original.premioManual) {
      input.premium_set_manually = form.premioManual;
    }
    if (form.distribuicao.trim() !== original.distribuicao) {
      input.distribution_code = form.distribuicao.trim();
    }
    return input;
  }

  const mudancas = pedido();
  const semMudanca = Object.keys(mudancas).length === 0;
  const apiError = atualizar.error instanceof ApiError ? atualizar.error : null;
  const grupo = operacao.modality_info?.group?.label;
  const minimoDaModalidade = operacao.modality_info?.min_premium;

  return (
    <div className="flex flex-col gap-5">
      {atualizar.error ? (
        <Alert tone="danger" title="Nao foi possivel atualizar a cotacao">
          {atualizar.error.message}
        </Alert>
      ) : null}

      <Card>
        <CardHeader
          title="Resumo da cotacao"
          description={`Aberta por ${operacao.created_by_label || 'quem abriu a operacao'}${operacao.updated_by_label ? `, alterada por ${operacao.updated_by_label}` : ''}.`}
        />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Dado
            rotulo="No da cotacao"
            valor={operacao.proposal_number ?? 'Numero ainda nao informado'}
            extra={
              operacao.proposal_number ? (
                <CopyButton value={operacao.proposal_number} label="Copiar numero" />
              ) : null
            }
          />
          <Dado rotulo="Data de cadastro" valor={formatDateTime(operacao.created_at)} />
          {/* NCT-215: o OnPoint diz "obrigatorio para prosseguir", sem fonte da regra;
              aqui e texto opcional, que nao trava nada. */}
          <Field
            label="Codigo do quadro de distribuicao"
            value={form.distribuicao}
            maxLength={60}
            disabled={!edita}
            hint="Opcional. Significado e obrigatoriedade a confirmar (NCT-215)."
            error={fieldErrorOf(apiError, 'distribution_code')}
            onChange={(event) => setForm({ ...form, distribuicao: event.target.value })}
          />
          <div className="min-w-0">
            <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
              Status
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {operacao.proposal_status ? (
                <StatusBadge kind="cotacao" value={operacao.proposal_status} />
              ) : null}
              {operacao.proposal_type ? (
                <StatusBadge kind="tipoCotacao" value={operacao.proposal_type} />
              ) : null}
            </div>
          </div>
          <Dado
            rotulo="Corretora"
            valor={operacao.broker?.label ?? 'Operacao direta'}
          />
          <Dado
            rotulo="CNPJ da Corretora"
            valor={
              operacao.broker?.document ? formatCpfCnpj(operacao.broker.document) : '-'
            }
          />
          <Dado rotulo="Produto" valor={operacao.product.label} />
          <Dado rotulo="Tomador" valor={operacao.policyholder.label} />
          <Dado
            rotulo="CNPJ tomador"
            valor={
              operacao.policyholder.document
                ? formatCpfCnpj(operacao.policyholder.document)
                : operacao.policyholder.sublabel
            }
            extra={
              can('tomadores.tomador.view') ? (
                <Link
                  to={`/tomadores/${operacao.policyholder.id}`}
                  className="text-xs font-medium text-brand-700 hover:underline"
                >
                  Visualizar cadastro completo do tomador
                </Link>
              ) : null
            }
          />
          <Dado
            rotulo="Responsavel"
            valor={operacao.owner_label || 'Sem responsavel'}
          />
          <Dado rotulo="Segurado" valor={operacao.insured?.label ?? 'Sem segurado'} />
          <Dado
            rotulo="CNPJ segurado"
            valor={operacao.insured ? formatCpfCnpj(operacao.insured.document) : '-'}
            extra={
              operacao.insured && can('segurados.segurado.view') ? (
                <Link
                  to={`/segurados/${operacao.insured.id}`}
                  className="text-xs font-medium text-brand-700 hover:underline"
                >
                  Visualizar cadastro completo do segurado
                </Link>
              ) : null
            }
          />
          {operacao.insured ? (
            <Select
              label="Selecionar Nome Fantasia"
              placeholder="Razao social do segurado"
              value={form.nomeFantasia}
              disabled={!podeNomeFantasia}
              error={fieldErrorOf(apiError, 'insured_trade_name_id')}
              hint={
                operacao.insured_trade_name && !operacao.insured_trade_name.id
                  ? `Gravado: ${operacao.insured_trade_name.name}. O nome saiu da lista do segurado.`
                  : undefined
              }
              options={(operacao.insured_trade_names ?? []).map((nome) => ({
                value: nome.id,
                label: `${nome.name}${nome.is_main ? ' (principal)' : ''}`,
              }))}
              onChange={(event) =>
                setForm({ ...form, nomeFantasia: event.target.value })
              }
            />
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title={`Modalidade Principal${grupo ? ` (${grupo})` : ''}`}
          description="O premio vem do valor garantido e da taxa aplicada. Inicio, prazo e fim ficam como digitados: a conta entre eles ainda e regra pendente."
        />
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <Dado rotulo="Modalidade Principal" valor={operacao.modality.label} />
          <MoneyField
            label="Importancia Segurada (R$)"
            value={form.valor}
            disabled={!podeValor}
            error={erros.valor ?? fieldErrorOf(apiError, 'insured_amount')}
            onValueChange={(texto) => setForm({ ...form, valor: texto })}
          />
          <Dado rotulo="Premio" valor={formatMoney(operacao.premium)} />
          <Field
            label="Inicio da vigencia"
            type="date"
            value={form.inicio}
            disabled={!podeDatas}
            error={fieldErrorOf(apiError, 'coverage_start')}
            onChange={(event) => setForm({ ...form, inicio: event.target.value })}
          />
          <Field
            label="Prazo (dias)"
            inputMode="numeric"
            value={form.prazo}
            disabled={!podeDatas}
            error={fieldErrorOf(apiError, 'term_days')}
            onChange={(event) =>
              setForm({ ...form, prazo: event.target.value.replace(/\D/g, '') })
            }
          />
          <Field
            label="Fim da vigencia"
            type="date"
            value={form.fim}
            disabled={!podeDatas}
            error={fieldErrorOf(apiError, 'coverage_end')}
            onChange={(event) => setForm({ ...form, fim: event.target.value })}
          />
          <Dado rotulo="Taxa aplicada" valor={formatRate(operacao.applied_rate_bps)} />
          <Dado rotulo="Comissao" valor={formatRate(operacao.commission_bps)} />
          <Dado
            rotulo="Prazo da etapa"
            valor={
              operacao.stage_due_at ? formatDate(operacao.stage_due_at) : 'Sem prazo'
            }
          />
          <Select
            label="Modalidade complementar"
            placeholder="Sem modalidade complementar"
            value={form.complementar}
            disabled={!podeComplementar}
            error={fieldErrorOf(apiError, 'complementary_modality_id')}
            options={opcoesComplementar}
            onChange={(event) => setForm({ ...form, complementar: event.target.value })}
          />
          <Field
            label="Inicio da vigencia da complementar"
            type="date"
            value={form.inicioComplementar}
            disabled={!podeDatas}
            error={fieldErrorOf(apiError, 'complementary_coverage_start')}
            onChange={(event) =>
              setForm({ ...form, inicioComplementar: event.target.value })
            }
          />
        </CardBody>
      </Card>

      <CoveragesSection
        operationId={operacao.id}
        canEditStructure={podeValor}
        canEditTerms={podeDatas}
        canEditPremium={podePremio}
      />

      {/* Termos contratuais da cotacao (P-COT-2), na ordem do OnPoint. */}
      <ParticularClausesSection operacao={operacao} />

      <ContractualTermsEditor operacao={operacao} />

      <QuoteConfigSection
        values={form.configuracao}
        disabled={!podeConfigurar}
        modality={operacao.modality_info}
        onChange={(configuracao) => setForm({ ...form, configuracao })}
      />

      <Card>
        <CardHeader
          title="Valores por alcada"
          description="Premio da principal e da complementar, premio minimo e comissao maxima sao digitados por quem tem a alcada: a regra que os calcularia ainda nao foi homologada. Ficam na linha do tempo e na trilha."
        />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <MoneyField
            label="Premio da modalidade principal (R$)"
            value={form.premioPrincipal}
            disabled={!podePremio}
            error={erros.premioPrincipal ?? fieldErrorOf(apiError, 'principal_premium')}
            onValueChange={(texto) => setForm({ ...form, premioPrincipal: texto })}
          />
          <MoneyField
            label="Premio da modalidade complementar (R$)"
            value={form.premioComplementar}
            disabled={!podePremio}
            error={
              erros.premioComplementar ??
              fieldErrorOf(apiError, 'complementary_premium')
            }
            onValueChange={(texto) => setForm({ ...form, premioComplementar: texto })}
          />
          <MoneyField
            label="Premio minimo (R$)"
            value={form.premioMinimo}
            disabled={!podePremio}
            error={erros.premioMinimo ?? fieldErrorOf(apiError, 'min_premium')}
            hint={
              minimoDaModalidade
                ? `Parametro da modalidade: ${formatMoney(minimoDaModalidade)}.`
                : undefined
            }
            onValueChange={(texto) => setForm({ ...form, premioMinimo: texto })}
          />
          <Field
            label="Comissao maxima (%)"
            inputMode="decimal"
            value={form.comissaoMaxima}
            disabled={!podePremio}
            error={erros.comissaoMaxima ?? fieldErrorOf(apiError, 'max_commission_bps')}
            onChange={(event) =>
              setForm({ ...form, comissaoMaxima: event.target.value })
            }
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={form.premioZerado}
              disabled={!podePremio}
              onChange={(event) =>
                setForm({ ...form, premioZerado: event.target.checked })
              }
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Premio zerado
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700">
            <input
              type="checkbox"
              checked={form.premioManual}
              disabled={!podePremio}
              onChange={(event) =>
                setForm({ ...form, premioManual: event.target.checked })
              }
              className="size-4 rounded border-line-strong text-brand-500 focus:ring-brand-500/25"
            />
            Premio definido manualmente
          </label>
        </CardBody>
      </Card>

      {/* Financeiro, emissao e validacoes automaticas da cotacao (P-COT-4). */}
      <InstallmentsSection operacao={operacao} />

      {edita || podeVerMinuta ? (
        <div className="flex flex-wrap justify-end gap-2">
          {podeVerMinuta ? (
            <Button variant="secondary" onClick={() => setBaixandoMinuta(true)}>
              Download da minuta
            </Button>
          ) : null}
          {edita ? (
            <>
              <Button
                variant="secondary"
                disabled={semMudanca || atualizar.isPending}
                onClick={() => setForm(formularioDe(operacao))}
              >
                Desfazer
              </Button>
              <Button
                loading={atualizar.isPending}
                disabled={semMudanca || invalido}
                onClick={() => atualizar.mutate(mudancas)}
              >
                Atualizar cotacao
              </Button>
            </>
          ) : null}
        </div>
      ) : null}

      {baixandoMinuta ? (
        <DraftPrintDialog
          operationId={operacao.id}
          title="Download da minuta"
          onClose={() => setBaixandoMinuta(false)}
        />
      ) : null}
    </div>
  );
}

function Dado({
  rotulo,
  valor,
  extra,
}: {
  rotulo: string;
  valor: string;
  extra?: ReactNode;
}) {
  return (
    <div className="min-w-0">
      <p className="text-2xs font-medium uppercase tracking-wide text-ink-500">
        {rotulo}
      </p>
      <p className="mt-0.5 truncate text-sm font-medium text-ink-900">{valor}</p>
      {extra ? <div className="mt-1">{extra}</div> : null}
    </div>
  );
}
